(() => {
if (customElements.get('collection-tabs')) return;

let featuredTabsUid = 0;

class CollectionTabs extends HTMLElement {
  connectedCallback () {
    this.build();
  }

  disconnectedCallback () {
    this.teardownTabSwiper();
  }

  rebuild () {
    this.teardownTabSwiper();
    const list = this.querySelector('.featured-collection__tabs');
    if (list) {
      const wrapper = list.querySelector('.swiper-wrapper');
      if (wrapper) wrapper.remove();
      list.classList.remove('swiper');
    }
    this._built = false;
    this.build();
  }

  build () {
    if (this._built) return;

    this._panels = Array.prototype.slice.call(
      this.querySelectorAll('[data-tab-panel]')
    );

    this.classList.add('is-ready');

    this.renderTabs();

    this._buttons = Array.prototype.slice.call(
      this.querySelectorAll('.featured-collection__tab')
    );
    if (this._buttons.length < 2 || this._panels.length < 2) {

      this.setupTabSwiper();
      return;
    }

    this._built = true;
    this._uid = ++featuredTabsUid;
    this._activeIndex = 0;

    this._buttons.forEach((button, index) => {
      const panel = this._panels[index];
      button.id = 'FeaturedTab-' + this._uid + '-' + index;
      button.setAttribute('role', 'tab');
      if (panel) {
        if (!panel.id) panel.id = 'FeaturedTabPanel-' + this._uid + '-' + index;
        button.setAttribute('aria-controls', panel.id);
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', button.id);
      }
      button.addEventListener('click', () => this.activate(index));
    });

    const nav = this.querySelector('.featured-collection__tabs');
    if (nav) {
      nav.addEventListener('keydown', this.handleKeydown.bind(this));
      nav.addEventListener('focusin', this.handleFocusIn.bind(this));
    }

    this.activate(0);
    this.setupTabSwiper();
    this.bindSharedNav();
  }

  /* ── Shared carousel arrows rendered in the tab row ─────────────── */
  sharedNavButtons () {
    const nav = this.querySelector('[data-fc-shared-nav]');
    if (!nav) return null;
    const prev = nav.querySelector('.swiper-button-prev');
    const next = nav.querySelector('.swiper-button-next');
    if (!prev || !next) return null;
    return { prev, next };
  }

  bindSharedNav () {
    if (this._sharedNavBound) return;
    const nav = this.sharedNavButtons();
    if (!nav) return;
    this._sharedNavBound = true;

    nav.prev.addEventListener('click', () => {
      const swiper = this.activeSwiper();
      if (swiper) swiper.slidePrev();
    });
    nav.next.addEventListener('click', () => {
      const swiper = this.activeSwiper();
      if (swiper) swiper.slideNext();
    });
  }

  activeSwiper () {
    const panel = this._panels && this._panels[this._activeIndex];
    if (!panel) return null;
    const carousel = panel.querySelector('[data-fc-swiper]');
    const swiper = carousel && carousel.swiperInstance;
    return swiper && !swiper.destroyed ? swiper : null;
  }

  hookSwiper (swiper) {
    if (!swiper || swiper._fcNavHooked) return;
    swiper._fcNavHooked = true;
    swiper.on('slideChange update resize lock unlock', () => this.updateSharedNav());
  }

  updateSharedNav () {
    const nav = this.sharedNavButtons();
    if (!nav) return;
    const swiper = this.activeSwiper();
    const locked = !swiper || swiper.isLocked;
    const loop = !!(swiper && swiper.params && swiper.params.loop);
    const prevDisabled = !locked && !loop && swiper.isBeginning;
    const nextDisabled = !locked && !loop && swiper.isEnd;

    nav.prev.classList.toggle('swiper-button-lock', locked);
    nav.next.classList.toggle('swiper-button-lock', locked);
    nav.prev.classList.toggle('swiper-button-disabled', prevDisabled);
    nav.next.classList.toggle('swiper-button-disabled', nextDisabled);
    nav.prev.setAttribute('aria-disabled', prevDisabled ? 'true' : 'false');
    nav.next.setAttribute('aria-disabled', nextDisabled ? 'true' : 'false');
  }

  /* section-common-carousel.js may initialise the panel swiper lazily
     (Swiper still loading, or on intersection). It stamps data-fc-init
     once the instance exists, so watch for that instead of polling. */
  afterSwiperReady (target, callback) {
    const ready = () => {
      const swiper = target.swiperInstance;
      return swiper && !swiper.destroyed ? swiper : null;
    };
    const now = ready();
    if (now) {
      callback(now);
      return;
    }
    if (target._fcNavObserver) target._fcNavObserver.disconnect();
    const observer = new MutationObserver(() => {
      const swiper = ready();
      if (!swiper) return;
      observer.disconnect();
      target._fcNavObserver = null;
      callback(swiper);
    });
    observer.observe(target, { attributes: true, attributeFilter: ['data-fc-init', 'class'] });
    target._fcNavObserver = observer;
  }

  renderTabs () {
    const list = this.querySelector('.featured-collection__tabs');
    if (!list || list.querySelector('.swiper-wrapper')) return;
    if (this._panels.length < 2) return;

    const showMedia = this.dataset.showMedia !== 'false';
    const placeholder = this.querySelector('[data-tab-placeholder]');
    const chevronTpl = this.querySelector('[data-tab-chevron]');

    const fragment = document.createDocumentFragment();

    this._panels.forEach((panel, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'featured-collection__tab swiper-slide ps-1 py-1';
      button.dataset.tabIndex = String(index);

      if (showMedia) {
        const media = document.createElement('span');
        media.className = 'featured-collection__tab-media';

        const src = panel.dataset.tabImage;
        const videoTpl = panel.querySelector('[data-tab-video-source]');

        if (panel.dataset.tabVideo === 'true' && videoTpl) {
          const clone = videoTpl.content.cloneNode(true);
          const video = clone.querySelector('video');
          if (video) {
            video.muted = true;
            video.playsInline = true;
            video.setAttribute('aria-hidden', 'true');
            video.tabIndex = -1;
          }
          media.appendChild(clone);
          if (video) {
            const played = video.play();
            if (played && typeof played.catch === 'function') played.catch(() => {});
          }
        } else if (src) {
          const img = document.createElement('img');
          img.className = 'featured-collection__tab-img';
          img.src = src;
          img.loading = 'lazy';
          img.width = 160;
          img.height = 160;
          img.alt = '';
          const srcset = panel.dataset.tabImageSrcset;
          if (srcset) {
            img.srcset = srcset;
            img.sizes = '48px';
          }
          media.appendChild(img);
        } else if (placeholder) {
          media.appendChild(placeholder.content.cloneNode(true));
        }

        button.appendChild(media);
      }

      const label = document.createElement('div');
      label.className = 'featured-collection__tab-label h5';

      const labelText = document.createElement('span');
      labelText.className = 'featured-collection__tab-ele';
      labelText.textContent = panel.dataset.tabTitle || '';
      label.appendChild(labelText);

      const count = panel.dataset.tabCount;
      if (count != null && count !== '') {
        const countEl = document.createElement('span');
        countEl.className = 'featured-collection__tab-count';
        const itemsLabel = this.dataset.itemsLabel || '';
        countEl.textContent = itemsLabel ? count + ' ' + itemsLabel : count;
        label.appendChild(countEl);
      }

      button.appendChild(label);

      if (chevronTpl) {
        const chevron = document.createElement('span');
        chevron.className = 'chevron d-none d-lg-block';
        chevron.setAttribute('aria-hidden', 'true');
        chevron.appendChild(chevronTpl.content.cloneNode(true));
        button.appendChild(chevron);
      }

      fragment.appendChild(button);
    });

    const wrapper = document.createElement('div');
    wrapper.className = 'swiper-wrapper';
    wrapper.setAttribute('role', 'tablist');
    if (this._tabsLabel == null) this._tabsLabel = list.getAttribute('aria-label') || '';
    if (this._tabsLabel) {
      wrapper.setAttribute('aria-label', this._tabsLabel);
      list.removeAttribute('aria-label');
    }
    list.removeAttribute('role');
    list.classList.add('swiper');

    const skeleton = list.querySelector('[data-tab-skeleton]');
    if (skeleton) skeleton.remove();

    wrapper.appendChild(fragment);
    list.appendChild(wrapper);
  }

  setupTabSwiper () {
    if (this._tabMql) return;

    const vertical = this.dataset.tabStyle === 'pills' || this.dataset.tabStyle === 'text';
    this._tabMql = window.matchMedia(vertical ? '(max-width: 991px)' : '(max-width: 1200px)');
    this._gridMql = window.matchMedia('(max-width: 992px)');

    this._onTabBreakpoint = () => {
      if (this._tabMql.matches) this.initTabSwiper();
      else this.destroyTabSwiper();
    };
    this._onGridBreakpoint = () => {
      if (this._gridMql.matches) this.initGridSwipers();
      else this.destroyGridSwipers();
    };

    if (this._tabMql.addEventListener) {
      this._tabMql.addEventListener('change', this._onTabBreakpoint);
      this._gridMql.addEventListener('change', this._onGridBreakpoint);
    } else {
      this._tabMql.addListener(this._onTabBreakpoint);
      this._gridMql.addListener(this._onGridBreakpoint);
    }
    this._onTabBreakpoint();
    this._onGridBreakpoint();
  }


  gridSwipers () {
    return Array.prototype.slice.call(
      this.querySelectorAll('[data-fc-grid-swipe]')
    );
  }

  initGridSwipers () {
    if (!this._gridMql || !this._gridMql.matches) return;
    const carousels = this.gridSwipers();
    if (!carousels.length) return;
    carousels.forEach((el) => {
      const panel = el.closest('[data-tab-panel]');
      if (panel && panel.hidden) return;
      if (window.SectionCarousel) window.SectionCarousel.reinit(el);
    });
  }

  destroyGridSwipers () {
    this.gridSwipers().forEach((el) => {
      if (el.swiperInstance) {
        el.swiperInstance.destroy(true, true);
        el.swiperInstance = null;
      }
      el.classList.remove('swiper-initialized');
    });
  }

  initTabSwiper (attempts) {
    if (!this._tabMql || !this._tabMql.matches) return;
    if (this._tabSwiper) return;
    if (this._buttons.length < 2) return;

    const list = this.querySelector('.featured-collection__tabs');
    if (!list || !list.querySelector('.swiper-wrapper')) return;

    if (!window.Swiper) {
      const tries = attempts || 0;
      if (tries >= 30 || !this.isConnected) return;
      setTimeout(() => this.initTabSwiper(tries + 1), 100);
      return;
    }

    this._tabSwiper = new window.Swiper(list, {
      slidesPerView: 'auto',
      spaceBetween: 0,
      watchOverflow: true,
      freeMode: true,
      slideToClickedSlide: true,
      a11y: false
    });

    if (typeof this._activeIndex === 'number') this._tabSwiper.slideTo(this._activeIndex, 0);
  }

  destroyTabSwiper () {
    if (!this._tabSwiper) return;
    if (!this._tabSwiper.destroyed) this._tabSwiper.destroy(true, true);
    this._tabSwiper = null;
    const list = this.querySelector('.featured-collection__tabs');
    if (list) list.classList.remove('swiper-initialized');
  }

  teardownTabSwiper () {
    this.destroyTabSwiper();
    this.destroyGridSwipers();
    this.querySelectorAll('[data-fc-swiper]').forEach((el) => {
      if (el._fcNavObserver) {
        el._fcNavObserver.disconnect();
        el._fcNavObserver = null;
      }
    });
    if (this._tabMql && this._onTabBreakpoint) {
      if (this._tabMql.removeEventListener) this._tabMql.removeEventListener('change', this._onTabBreakpoint);
      else this._tabMql.removeListener(this._onTabBreakpoint);
    }
    if (this._gridMql && this._onGridBreakpoint) {
      if (this._gridMql.removeEventListener) this._gridMql.removeEventListener('change', this._onGridBreakpoint);
      else this._gridMql.removeListener(this._onGridBreakpoint);
    }
    this._tabMql = null;
    this._gridMql = null;
    this._onTabBreakpoint = null;
    this._onGridBreakpoint = null;
  }

  activate (index) {
    this._buttons.forEach((button, i) => {
      const selected = i === index;
      button.setAttribute('aria-selected', selected ? 'true' : 'false');
      if (this._panels[i]) this._panels[i].hidden = !selected;
    });
    this._activeIndex = index;
    if (this._tabSwiper && !this._tabSwiper.destroyed) this._tabSwiper.slideTo(index);
    this.syncCarousel(index);
  }


  syncCarousel (index) {
    const panel = this._panels[index];
    if (!panel) return;

    const carousel = panel.querySelector('[data-fc-swiper]');
    const gridSwipe = panel.querySelector('[data-fc-grid-swipe]');
    const target = carousel || (this._gridMql && this._gridMql.matches ? gridSwipe : null);
    if (!target) return;

    requestAnimationFrame(() => {
      if (target.swiperInstance) {
        target.swiperInstance.update();
        target.swiperInstance.slideTo(0, 0);
      } else if (window.SectionCarousel) {
        window.SectionCarousel.reinit(target);
      }
      if (carousel && this.sharedNavButtons()) {
        this.afterSwiperReady(carousel, (swiper) => {
          this.hookSwiper(swiper);
          this.updateSharedNav();
        });
      }
    });
  }

  focusedTabIndex (target) {
    const button = target && target.closest ? target.closest('.featured-collection__tab') : null;
    return button ? this._buttons.indexOf(button) : -1;
  }

  handleKeydown (event) {
    const count = this._buttons.length;
    const current = this.focusedTabIndex(event.target);
    if (current < 0) return;
    let next = null;
    if (event.key === 'ArrowRight') next = (current + 1) % count;
    else if (event.key === 'ArrowLeft') next = (current - 1 + count) % count;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = count - 1;
    if (next === null) return;
    event.preventDefault();
    this._buttons[next].focus();
  }

  handleFocusIn (event) {
    const index = this.focusedTabIndex(event.target);
    if (index < 0) return;
    const list = this.querySelector('.featured-collection__tabs');
    if (list) list.scrollLeft = 0;
    if (this._tabSwiper && !this._tabSwiper.destroyed) this._tabSwiper.slideTo(index);
  }
}

customElements.define('collection-tabs', CollectionTabs);

document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('collection-tabs').forEach((el) => el.rebuild());
});

document.addEventListener('shopify:block:select', (event) => {
  const root = event.target.closest('collection-tabs');
  if (!root || !root._buttons) return;
  const index = root._panels.indexOf(event.target.closest('[data-tab-panel]'));
  if (index > -1) root.activate(index);
});
})();
